import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import BalanceChart from "@/components/application/BalanceChart";
import MonthlyBalanceCards from "@/components/application/MonthlyBalanceCards";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  balanceTone,
  balancesBeforeCurrentMonth,
  countPossibleMissedFromOffer,
  dateKeyLocal,
  isWeekendDate,
  lastBusinessDayKeys,
  parseLocalDate,
  paymentToDailyAmount,
} from "@/utils/balanceLowThreshold";

/** Anchor id for scroll-into-view from the offer-calculator callout. */
export const DAILY_BALANCES_SECTION_ID = "daily-balances";

interface DailyBalanceProps {
  data: Array<{ date: string; balance: number }>;
  /** Chart theoretical-balance line (AI offer daily, or live offer daily). */
  dailyPayment?: number;
  nsfCount?: number;
  /** ISO dates of NSF transactions for per-month NSF chips. */
  nsfDates?: string[];
  /** Live offer-calc payment field (installment), for low-balance coloring + flag. */
  offerPayment?: number | null;
  offerFrequency?: string | null;
  /** Term in days — trailing business-day window size. */
  offerTerm?: number | null;
}

const CHIP_BASE =
  "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[0.65rem] font-semibold";

/** Soft pastel tones aligned with the site gradient background. */
const CHIP_NEGATIVE = "border-rose-200/80 bg-rose-100 text-rose-700";
const CHIP_AMBER = "border-amber-200/80 bg-amber-100 text-amber-800";
const CHIP_NSF = "border-violet-200/80 bg-violet-100 text-violet-700";

/** Kill default TooltipContent enter/exit animations for instant show/hide. */
const TIP_INSTANT =
  "animate-none duration-0 data-[state=closed]:animate-none data-[side=bottom]:slide-in-from-top-0 data-[side=left]:slide-in-from-right-0 data-[side=right]:slide-in-from-left-0 data-[side=top]:slide-in-from-bottom-0";

type BannerChipProps = {
  label: React.ReactNode;
  tip: string;
  className?: string;
  tipClassName?: string;
};

/** Flat banner pill with hover-only tooltip (instant open/close). */
function BannerChip({ label, tip, className, tipClassName }: BannerChipProps) {
  return (
    <Tooltip disableHoverableContent>
      <TooltipTrigger asChild>
        <span className={cn(CHIP_BASE, className)}>{label}</span>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        className={cn("max-w-[220px] text-xs", TIP_INSTANT, tipClassName)}
      >
        {tip}
      </TooltipContent>
    </Tooltip>
  );
}

const DailyBalance: React.FC<DailyBalanceProps> = ({
  data,
  dailyPayment,
  nsfCount,
  nsfDates = [],
  offerPayment,
  offerFrequency,
  offerTerm,
}) => {
  const [graphOpen, setGraphOpen] = useState(false);

  const filteredData = useMemo(() => balancesBeforeCurrentMonth(data), [data]);

  const totalNegDays = useMemo(
    () => filteredData.filter((d) => d.balance < 0).length,
    [filteredData]
  );

  const offerDaily = useMemo(() => {
    if (offerPayment == null || !(Number(offerPayment) > 0)) return 0;
    return paymentToDailyAmount(Number(offerPayment), offerFrequency ?? "daily");
  }, [offerPayment, offerFrequency]);

  const termDays = useMemo(() => {
    const t = Number(offerTerm);
    return Number.isFinite(t) && t > 0 ? Math.floor(t) : 0;
  }, [offerTerm]);

  const offerLowBalance = useMemo(() => {
    if (!(offerDaily > 0) || !(termDays > 0)) return null;
    return {
      dailyPayment: offerDaily,
      term: termDays,
      dynamicDayKeys: lastBusinessDayKeys(filteredData, termDays),
    };
  }, [offerDaily, termDays, filteredData]);

  const totalLowDays = useMemo(() => {
    const dynamicDayKeys = offerLowBalance?.dynamicDayKeys;
    return filteredData.filter((d) => {
      const dt = parseLocalDate(d.date);
      if (!dt) return false;
      const weekend = isWeekendDate(dt);
      const dynamicDay =
        dynamicDayKeys != null && offerLowBalance != null && dynamicDayKeys.has(dateKeyLocal(dt));
      return (
        balanceTone(d.balance, {
          dailyPayment: offerLowBalance?.dailyPayment,
          dynamicDay,
          weekend,
        }) === "low"
      );
    }).length;
  }, [filteredData, offerLowBalance]);

  const resolvedNsfCount = useMemo(() => {
    if (nsfCount !== undefined) return nsfCount;
    return nsfDates.length;
  }, [nsfCount, nsfDates]);

  const possibleMissed = useMemo(
    () => countPossibleMissedFromOffer(data, offerPayment, offerFrequency, offerTerm),
    [data, offerPayment, offerFrequency, offerTerm]
  );

  /** Prefer live offer daily for the chart line when available. */
  const chartDailyPayment = offerDaily > 0 ? offerDaily : dailyPayment;

  if (!filteredData.length) return null;

  return (
    <Collapsible open={graphOpen} onOpenChange={setGraphOpen}>
      <div
        id={DAILY_BALANCES_SECTION_ID}
        className="mb-2 flex w-full scroll-mt-4 flex-wrap items-center gap-2"
      >
        <p className="font-sans text-lg font-semibold text-foreground">Daily Balances</p>
        <TooltipProvider delayDuration={0} skipDelayDuration={0}>
          {totalNegDays > 0 && (
            <BannerChip
              className={CHIP_NEGATIVE}
              label={`${totalNegDays} Negative Days`}
              tip="Days where the account balance dropped below $0."
            />
          )}
          {/* Possible-missed replaces the low-balance banner when both would apply. */}
          {offerLowBalance && possibleMissed > 0 ? (
            <BannerChip
              className={CHIP_AMBER}
              tipClassName="max-w-[260px]"
              label={`${possibleMissed} Possible Missed Payment${possibleMissed !== 1 ? "s" : ""}`}
              tip="Shows how many payments the merchant would have missed if this deal was historical."
            />
          ) : (
            totalLowDays > 0 && (
              <BannerChip
                className={CHIP_AMBER}
                label={`${totalLowDays} Low Balance Days`}
                tip="Days where the balance stayed at or below the low-balance threshold (amber in the month cards)."
              />
            )
          )}
          {resolvedNsfCount > 0 && (
            <BannerChip
              className={CHIP_NSF}
              label={`${resolvedNsfCount} NSF`}
              tip="Non-Sufficient Funds — transactions declined due to insufficient account balance."
            />
          )}
        </TooltipProvider>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            aria-expanded={graphOpen}
            className="flex shrink-0 items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {graphOpen ? (
              <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
            )}
            Balance graph
          </button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        <div className="mb-2">
          <BalanceChart data={filteredData} dailyPayment={chartDailyPayment} />
        </div>
      </CollapsibleContent>

      <MonthlyBalanceCards
        data={filteredData}
        offerLowBalance={offerLowBalance}
        nsfDates={nsfDates}
      />
    </Collapsible>
  );
};

export default DailyBalance;
